import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { createBadgeNotification, createMilestoneNotification } from './notificationService';

// Streak levels and rewards
export const STREAK_LEVELS = {
  1: {
    name: 'Spark',
    icon: '✨',
    reward: 'onboarding_tip',
    description: 'You lit the spark!',
    badge: null
  },
  3: {
    name: 'Ember',
    icon: '🔥',
    reward: 'badge',
    description: 'Keep the fire burning!',
    badge: {
      id: 'ember_streak',
      name: '3-Day Streak',
      icon: '🔥',
      description: 'Maintained activity for 3 consecutive days'
    }
  },
  7: {
    name: 'Flame',
    icon: '🔥',
    reward: 'profile_banner',
    description: 'You\'re on fire!',
    badge: {
      id: 'flame_streak',
      name: '7-Day Flame',
      icon: '🔥',
      description: 'One week of consistent engagement'
    }
  },
  14: {
    name: 'Inferno',
    icon: '🔥',
    reward: 'special_emoji',
    description: 'Burning bright for two weeks!',
    badge: {
      id: 'inferno_streak',
      name: '14-Day Inferno',
      icon: '🔥',
      description: 'Two weeks of dedication'
    }
  },
  30: {
    name: 'Eternal Debate',
    icon: '🔥',
    reward: 'legendary_badge',
    description: 'A true debate legend!',
    badge: {
      id: 'eternal_debate',
      name: 'Eternal Debate 🔥',
      icon: '🔥',
      description: 'Legendary 30-day streak master'
    }
  }
};

// Activity types that count toward streaks (simplified to app visits)
export const STREAK_ACTIVITIES = {
  APP_VISIT: 'app_visit'
};

// Get user's current streak data
export const getUserStreak = async (userId) => {
  try {
    const streakRef = doc(db, 'users', userId, 'streak', 'current');
    const streakDoc = await getDoc(streakRef);
    
    if (streakDoc.exists()) {
      const data = streakDoc.data();
      return {
        lastActivity: data.lastActivity?.toDate() || null,
        currentStreak: data.currentStreak || 0,
        longestStreak: data.longestStreak || 0,
        lastActivityType: data.lastActivityType || null,
        todayActivities: data.todayActivities || [],
        forgivenesTokensUsed: data.forgivenesTokensUsed || 0,
        streakStartDate: data.streakStartDate?.toDate() || null
      };
    }
    
    return {
      lastActivity: null,
      currentStreak: 0,
      longestStreak: 0,
      lastActivityType: null,
      todayActivities: [],
      forgivenesTokensUsed: 0,
      streakStartDate: null
    };
  } catch (error) {
    console.error('Error getting user streak:', error);
    return {
      lastActivity: null,
      currentStreak: 0,
      longestStreak: 0,
      lastActivityType: null,
      todayActivities: [],
      forgivenesTokensUsed: 0,
      streakStartDate: null
    };
  }
};

// Check if two dates are the same day
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

// Check if date is yesterday
const isYesterday = (date, today = new Date()) => {
  if (!date) return false;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
};

// Calculate streak based on last activity
const calculateStreak = (lastActivity, currentStreak, today = new Date()) => {
  if (!lastActivity) {
    return { newStreak: 1, streakBroken: false };
  }
  
  if (isSameDay(lastActivity, today)) {
    // Same day - maintain current streak
    return { newStreak: currentStreak, streakBroken: false };
  }
  
  if (isYesterday(lastActivity, today)) {
    // Yesterday - increment streak
    return { newStreak: currentStreak + 1, streakBroken: false };
  }
  
  // More than 1 day gap - streak broken
  return { newStreak: 1, streakBroken: true };
};

// Update user streak (simplified for daily app visits)
export const updateUserStreak = async (userId) => {
  try {
    const streakRef = doc(db, 'users', userId, 'streak', 'current');
    const currentStreak = await getUserStreak(userId);
    const today = new Date();
    const todayStr = today.toDateString();
    
    // Check if user already visited today
    if (currentStreak.lastActivity && isSameDay(currentStreak.lastActivity, today)) {
      // Already visited today - no streak update needed
      return currentStreak;
    }
    
    // Calculate new streak
    const { newStreak, streakBroken } = calculateStreak(
      currentStreak.lastActivity,
      currentStreak.currentStreak,
      today
    );
    
    // Update longest streak if needed
    const newLongestStreak = Math.max(currentStreak.longestStreak, newStreak);
    
    // Prepare update data
    const updateData = {
      lastActivity: serverTimestamp(),
      currentStreak: newStreak,
      longestStreak: newLongestStreak,
      lastActivityType: STREAK_ACTIVITIES.APP_VISIT,
      updatedAt: serverTimestamp()
    };
    
    // Set streak start date if this is a new streak
    if (newStreak === 1 && (streakBroken || !currentStreak.streakStartDate)) {
      updateData.streakStartDate = serverTimestamp();
    }
    
    // Update Firestore
    await setDoc(streakRef, updateData, { merge: true });
    
    // Check for milestone rewards
    const previousStreak = currentStreak.currentStreak;
    await checkStreakMilestones(userId, newStreak, previousStreak);
    
    return {
      ...currentStreak,
      lastActivity: today,
      currentStreak: newStreak,
      longestStreak: newLongestStreak,
      lastActivityType: STREAK_ACTIVITIES.APP_VISIT,
      streakBroken
    };
    
  } catch (error) {
    console.error('Error updating user streak:', error);
    throw error;
  }
};

// Check for streak milestones and award badges/notifications
const checkStreakMilestones = async (userId, newStreak, previousStreak) => {
  try {
    // Check if we hit a new milestone
    for (const [milestone, level] of Object.entries(STREAK_LEVELS)) {
      const milestoneNum = parseInt(milestone);
      
      if (newStreak >= milestoneNum && previousStreak < milestoneNum) {
        // Hit a new milestone!
        
        // Create milestone notification
        await createMilestoneNotification(
          userId,
          'streak',
          newStreak
        );
        
        // Award badge if applicable
        if (level.badge) {
          await createBadgeNotification(
            userId,
            level.badge.name,
            level.badge.icon
          );
        }
        
        // Store milestone achievement
        const milestoneRef = doc(db, 'users', userId, 'achievements', `streak_${milestoneNum}`);
        await setDoc(milestoneRef, {
          type: 'streak_milestone',
          milestone: milestoneNum,
          level: level.name,
          achievedAt: serverTimestamp(),
          badge: level.badge || null,
          reward: level.reward
        });
      }
    }
  } catch (error) {
    console.error('Error checking streak milestones:', error);
  }
};

// Get streak level info based on current streak
export const getStreakLevel = (currentStreak) => {
  let level = STREAK_LEVELS[1]; // Default to Spark
  
  for (const [milestone, levelInfo] of Object.entries(STREAK_LEVELS)) {
    if (currentStreak >= parseInt(milestone)) {
      level = levelInfo;
    }
  }
  
  return level;
};

// Get next milestone info
export const getNextMilestone = (currentStreak) => {
  for (const [milestone, level] of Object.entries(STREAK_LEVELS)) {
    if (currentStreak < parseInt(milestone)) {
      return {
        days: parseInt(milestone),
        level,
        daysLeft: parseInt(milestone) - currentStreak
      };
    }
  }
  
  // If past all milestones
  return null;
};

// Use forgiveness token (optional feature)
export const useForgivenesToken = async (userId) => {
  try {
    const streakRef = doc(db, 'users', userId, 'streak', 'current');
    const currentStreak = await getUserStreak(userId);
    
    if (currentStreak.forgivenesTokensUsed >= 1) {
      throw new Error('Already used forgiveness token this month');
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    await updateDoc(streakRef, {
      lastActivity: serverTimestamp(),
      forgivenesTokensUsed: currentStreak.forgivenesTokensUsed + 1,
      lastActivityType: 'forgiveness_token',
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error using forgiveness token:', error);
    throw error;
  }
};

// Reset monthly forgiveness tokens (would be called by a scheduled function)
export const resetMonthlyTokens = async (userId) => {
  try {
    const streakRef = doc(db, 'users', userId, 'streak', 'current');
    await updateDoc(streakRef, {
      forgivenesTokensUsed: 0,
      tokensResetAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error resetting monthly tokens:', error);
    throw error;
  }
};

// Get streak stats for display
export const getStreakStats = async (userId) => {
  try {
    const streakData = await getUserStreak(userId);
    const currentLevel = getStreakLevel(streakData.currentStreak);
    const nextMilestone = getNextMilestone(streakData.currentStreak);
    
    return {
      ...streakData,
      currentLevel,
      nextMilestone,
      isOnStreak: streakData.currentStreak > 0,
      streakIntensity: Math.min(streakData.currentStreak / 30, 1) // For flame animation intensity
    };
  } catch (error) {
    console.error('Error getting streak stats:', error);
    return null;
  }
};